import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors'
import type { GraphQLFormattedError } from 'graphql'

/**
 * Lectores de errores de Apollo Client **v4**.
 *
 * En v4 el `ApolloError` de v3 (con `.graphQLErrors` y `.networkError` sueltos)
 * ya no existe: un fallo de una operación lanza una de estas clases:
 * - `CombinedGraphQLErrors` — el servidor respondió con errores en `errors`
 *   (aquí viven el `message` y el `extensions.code` que pone el gateway).
 * - `ServerError` / `ServerParseError` — problema a nivel HTTP (5xx, HTML de
 *   error, JSON malformado): el servidor no devolvió un cuerpo GraphQL válido.
 * - Un `Error` suelto cuando `fetch` se rechaza (sin conexión, DNS, CORS).
 *
 * Leer `err.graphQLErrors`/`err.networkError` en v4 devuelve `undefined`, que es
 * justo el bug que hacía que cualquier error de login se mostrara como
 * "Error inesperado".
 */

const NETWORK_MESSAGE_RE = /failed to fetch|networkerror|network error|load failed/i

/**
 * `true` si el error es de transporte/red (el servidor no respondió con un
 * cuerpo GraphQL válido), no una respuesta GraphQL de negocio.
 */
export function isConnectivityError(err: unknown): boolean {
  if (ServerError.is(err) || ServerParseError.is(err)) return true
  // `fetch` rechazado sin respuesta: Error suelto (no un CombinedGraphQLErrors).
  if (
    err instanceof Error &&
    !CombinedGraphQLErrors.is(err) &&
    NETWORK_MESSAGE_RE.test(err.message)
  ) {
    return true
  }
  return false
}

/** El primer error GraphQL devuelto por el gateway, o `null` si no es uno. */
export function firstGraphQLError(err: unknown): GraphQLFormattedError | null {
  if (!CombinedGraphQLErrors.is(err)) return null
  return err.errors[0] ?? null
}

/** El `extensions.code` que puso el gateway (NOT_FOUND, FORBIDDEN, …). */
export function graphQLErrorCode(err: unknown): string | undefined {
  const code = firstGraphQLError(err)?.extensions?.['code']
  return typeof code === 'string' ? code : undefined
}

/** El `message` del primer error GraphQL, o cadena vacía si no lo hay. */
export function graphQLErrorMessage(err: unknown): string {
  return firstGraphQLError(err)?.message ?? ''
}
